import type { GeneratedItem, ItemId } from '@/types/domain';
import { jaccard, normalizeName, stringSimilarity } from '@/utils/text';

export interface DuplicateMatch {
  isDuplicate: boolean;
  score: number;
  existing?: GeneratedItem;
  reason: string;
}

/**
 * Prevents the model from creating slightly-renamed duplicates (e.g.
 * "Reinforced Pickaxe" vs "Strong Pickaxe"). Combines normalized-name similarity,
 * tag overlap and shared ingredient lineage.
 */
export function findDuplicate(
  candidates: GeneratedItem[],
  name: string,
  tags: string[],
  ingredientIds: ItemId[],
  threshold = 0.78,
): DuplicateMatch {
  const normName = normalizeName(name);
  let best: DuplicateMatch = { isDuplicate: false, score: 0, reason: '' };

  for (const item of candidates) {
    const nameSim = normName && item.name ? stringSimilarity(normName, normalizeName(item.name)) : 0;
    const tagSim = jaccard(tags, item.tags);
    const lineageSim = jaccard(ingredientIds, item.ingredientIds);

    // A core-name match (e.g. both reduce to "pickaxe") is a strong duplicate signal.
    const sameCore = normName && normalizeName(item.name) === normName && normName.length > 0;
    const score = Math.max(
      (sameCore ? 0.85 : 0) + tagSim * 0.1 + lineageSim * 0.05,
      nameSim * 0.55 + tagSim * 0.3 + lineageSim * 0.15,
    );

    if (score > best.score) {
      best = {
        isDuplicate: score >= threshold,
        score,
        existing: item,
        reason: sameCore
          ? `Same core concept as "${item.name}" (${Math.round(score * 100)}%).`
          : `Similar to "${item.name}" (${Math.round(score * 100)}%).`,
      };
    }
  }
  return best;
}

export function hasNearDuplicate(candidates: GeneratedItem[], name: string, tags: string[], ingredientIds: ItemId[]): boolean {
  return findDuplicate(candidates, name, tags, ingredientIds).isDuplicate;
}
